<template>
  <div class="create-whats-new__wrapper">
    <span class="create-whats-new__now-you-can-text q-mb-md">Now you can:</span>
    <mi-infinite-scroll
      ref="miInfiniteScroll"
      class="mi-infinite-scroll"
    >
      <mi-text-field
        v-model="newWhatsNewModalData.header"
        class="q-mb-sm q-mt-md"
        label="Header"
        placeholder="- Features description..."
        outlined
      ></mi-text-field>
      <mi-text-field
        v-model="newWhatsNewModalData.description"
        class="q-mb-sm"
        label="Brief description"
        placeholder="- Features description..."
        outlined
      ></mi-text-field>

      <div v-for="(stepData, idx) in newWhatsNewModalData.steps" :key="stepData.step">
        <mi-text-field
          v-model="stepData.description"
          class="q-pb-sm"
          :label="`Step ${ stepData.step } description`"
          placeholder="Short description of step"
          outlined
        ></mi-text-field>

        <q-uploader
          accept="image/*"
          :auto-upload="false"
          no-thumbnails
          :max-files="1"
          class="step-image-uploader"
          :max-file-size="130 * 1024"
          @rejected="onRejected"
          @added="files => onAdded(idx, files)"
          @removed="() => onRemoved(idx)"
        >
          <!-- HEADER CUSTOM -->
          <template #header="scope">
            <div class="step-image-uploader__header row">
              <div class="step-image-uploader__header-left">
                <span class="label">Upload File</span>
                <span class="progress"> {{ scope?.uploadSizeLabel }} / {{ scope?.uploadProgressLabel }}</span>
              </div>
              <div class="step-image-uploader__header-right">
                <q-btn
                  v-if="scope.canAddFiles"
                  icon="plus-circle"
                  @click="scope.pickFiles"
                >
                  <q-uploader-add-trigger></q-uploader-add-trigger>
                </q-btn>
              </div>
            </div>
          </template>

          <template #list="scope">
            <div v-if="scope.files.length > 0" class="step-image-uploader__file-row row">
              <div class="file-name ellipsis">{{ scope.files[0].name }}</div>
              <q-btn flat dense round icon="close" @click="scope.removeFile(scope.files[0])"></q-btn>
            </div>
          </template>
        </q-uploader>
      </div>
    </mi-infinite-scroll>
    <div class="create-whats-new__actions row q-mt-md ">
      <mi-btn class="preview-modal-btn" @click="enablePreviewModal"> Preview Modal </mi-btn>
      <mi-btn color="accent" @click="submitWhatsNewModal"> Submit modal </mi-btn>
    </div>
  </div>
  <check-whats-new-modal v-if="previewingModal" :data="newWhatsNewModalData"></check-whats-new-modal>
</template>

<script>
  import { onBeforeUnmount, provide, reactive, ref } from 'vue'
  import CheckWhatsNewModal from '../../views/CheckWhatsNew.vue'
  import MiInfiniteScroll from '../../packages/@mi-library/MiInfiniteScroll/MiInfiniteScroll.vue'
  import { addWhatsNewData } from '../../api'
  import notify from '../../utils/notify'

  export default {
    name: 'CreateWhatsNewModal',
    components: { MiInfiniteScroll, CheckWhatsNewModal },
    setup() {
      const newWhatsNewModalData = reactive({
        header: '',
        description: '',
        steps: [
          {
            description: '',
            image: '',
            step: 1
          },
          {
            description: '',
            image: '',
            step: 2
          },
          {
            description: '',
            image: '',
            step: 3
          }
        ]
      })

      const previewFiles = reactive([[], [], []])

      const uploader = ref(null)
      const previewingModal = ref(false)
      provide('previewingModal', previewingModal)

      const enablePreviewModal = () => {
        previewingModal.value = true
      }

      function fileToBase64(file) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => resolve(reader.result)
          reader.onerror = err => reject(err)
        })
      }

      const urlByStep = new Map()

      // eslint-disable-next-line no-underscore-dangle
      const toNativeFile = item => item?.__file ?? item

      const onAdded = async (idx, list) => {
        const first = Array.isArray(list) && list.length ? toNativeFile(list[0]) : null
        if (first) {
          newWhatsNewModalData.steps[idx].image = await fileToBase64(first)
        }
        else {
          newWhatsNewModalData.steps[idx].image = ''
        }
      }

      function onRejected(rejectedEntries) {
        rejectedEntries.forEach(({ failedPropValidation, file }) => {
          if (failedPropValidation === 'max-file-size') {
            notify({
              type: 'negative',
              title: 'File size exceeded',
              content: `the file "${ file?.name ?? '' }" exceeds 130 KB.`
            })
          }
        })
      }

      const hasFile = idx => newWhatsNewModalData.steps[idx].image !== ''

      const onRemoved = idx => {
        const prev = urlByStep.get(idx)
        if (prev) {
          URL.revokeObjectURL(prev)
          urlByStep.delete(idx)
        }
        newWhatsNewModalData.steps[idx].image = ''
      }

      const submitWhatsNewModal = async () => {
        try {
          await addWhatsNewData(newWhatsNewModalData, { highlightFlag: true })
          notify({
            title: 'COMPLETED',
            content: 'Whats new modal was successfully created.',
            type: 'dark',
            progress: false
          })
        }
        catch (e) {
          notify({
            title: 'ERROR',
            content: 'Error creating new whats new modal.',
            type: 'error',
            progress: false
          })
        }
      }

      onBeforeUnmount(() => {
        urlByStep.forEach(u => URL.revokeObjectURL(u))
        urlByStep.clear()
      })

      return {
        newWhatsNewModalData,
        previewingModal,
        enablePreviewModal,
        uploader,
        previewFiles,
        onAdded,
        onRemoved,
        hasFile,
        submitWhatsNewModal,
        onRejected
      }
    }

  }
</script>

<style lang="scss" scoped>
  // stylelint-disable declaration-no-important
  ::v-deep(.q-uploader) {
    margin-bottom: 24px;
    width: 100% !important;
  }

  .mi-infinite-scroll {
    overflow: auto;
    max-height: calc(100vh - 375px);
    overflow-y: scroll;
    padding-bottom: 1px;
  }

  .step-image-uploader {
    &__header {
      justify-content: space-between;
      height: 25px;
      align-content: center;
    }

    &__header-left {
      font-family: $mi-typography-font-family;
      font-size: 14px;
      line-height: 17px;
      vertical-align: middle;
      padding: 0 8px;
      align-content: center;

      .label {
        margin-right: 8px;
        font-weight: 700;
      }

      .progress {
        font-size: 12px;
      }
    }

    ::v-deep(.q-uploader__list) {
      max-height: 40px;
      min-height: 0;
    }

    .step-image-uploader {
      &__file-row {
        background-color: #e6e6e6;
        justify-content: space-between;

        .q-btn {
          font-size: 10px;
        }

        .file-name {
          font-family: $mi-typography-font-family;
          font-weight: 400;
          font-size: 14px;
          line-height: 21px;
          text-align: center;
          vertical-align: middle;
        }
      }
    }

    &__header-right {
      .q-btn {
        font-size: 10px;
      }
    }
  }

  .preview-modal-btn {
    background-color: #d3d3d3 !important;
    color: #303c49 !important;
    margin-right: 16px;
  }

  .create-whats-new {
    &__wrapper {
      padding: 0 !important;
      margin-left: 0 !important;
      color: $mi-anthracite-800;
      font-family: $mi-typography-font-family-condensed;
    }

    &__now-you-can-text {
      font-weight: 700;
      font-size: 18px;
      line-height: 27px;
      text-transform: uppercase;
      color: $mi-red-500;
    }

    &__title {
      width: 176px;
      height: 27px;
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 27px;
      text-transform: uppercase;
      flex: none;
      order: 0;
      flex-grow: 0;
    }

    &__form {
      margin-top: 32px;

      .q-field:nth-child(1) {
        width: 45%;
      }

      .q-field:nth-child(2) {
        width: 85%;
      }

      .mi-select-margin {
        margin-bottom: 32px;
      }
    }

    &__submit {
      margin: 32px 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;

      &--checkbox {
        margin-left: 24px;
        font-family: $mi-typography-font-family;
      }
    }

    &__divider {
      height: 2px;
      width: 85%;
      margin: 32px 0;
      background-color: $mi-anthracite-50;
    }
  }
</style>
